import React, { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AsyncCheckPillProps } from '../../../../../../../components';
import * as services from '../../../../../../../components/FilterSection/services';
import { optimizationPricesResultSelector } from '../../../../../../../reducers/OtimizacaoPrecos';
import { HistoryFiltersType, setHistoryFilters } from '../../../../../../../reducers/OtimizacaoPrecos/optimizationPricesResult';
import { checkIfFiltersValuesIsEmpty } from '../../../../../../../utils';
import { listResponsible } from '../../../../services';

export type FilterObject<K extends string = string> = {
  dataKey: K;
} & AsyncCheckPillProps;

type Queries = FilterObject<HistoryFiltersType>[];

export const useHistoryFilters = () => {
  const dispatch = useDispatch();

  const values = useSelector(optimizationPricesResultSelector, shallowEqual);
  const { productsFamilyId, productsId, responsibleId } = values.tabs.history.filters;

  const showOnlyDefaultCategoryLevel = true;

  const set = useCallback(
    (key: HistoryFiltersType, value: string[]) => {
      dispatch(setHistoryFilters({ filter: key, data: value }));
    },
    [dispatch],
  );

  const handleClean = useCallback(
    (key: HistoryFiltersType) => {
      dispatch(setHistoryFilters({ filter: key, data: [] }));
    },
    [dispatch],
  );

  const queries: Queries = React.useMemo(
    () => [
      {
        dataKey: 'productsId',
        placeholder: 'Produto',
        queryFn: services.listProducts,
        value: productsId,
        onSelect: value => set('productsId', value),
        onClean: () => handleClean('productsId'),
      },
      {
        dataKey: 'productsFamilyId',
        placeholder: 'Família',
        queryFn: services.listFamilies,
        value: productsFamilyId,
        onSelect: value => set('productsFamilyId', value),
        onClean: () => handleClean('productsFamilyId'),
      },
      {
        dataKey: 'responsibleId',
        placeholder: 'Responsável',
        queryFn: listResponsible,
        value: responsibleId,
        onSelect: value => set('responsibleId', value),
        onClean: () => handleClean('responsibleId'),
      },
    ],
    [productsFamilyId, productsId, responsibleId, set, handleClean],
  );

  const filtersWithSegments = React.useMemo(() => [...queries], [queries]);

  const memoValues = React.useMemo(() => values, [values]);

  const isEmpty = checkIfFiltersValuesIsEmpty({ productsFamilyId, productsId, responsibleId });

  return {
    list: filtersWithSegments,
    values: memoValues,
    showOnlyDefaultCategoryLevel,
    isEmpty,
  } as const;
};
